import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Employees as GroupIcon,
} from "@/components/icons";
import type { TableColumn } from "@/types/component";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { getAdminUsers } from "@/lib/users.api";
import { userGroupsApi } from "../services/user-groups.api";
import UserGroupCreateModal from "../components/UserGroupCreateModal";
import UserGroupMembersPanel from "../components/UserGroupMembersPanel";
import type {
  UserGroupDetail,
  UserGroupListItem,
  UserGroupMember,
} from "../types/user-groups.types";
import styles from "./UserGroupsPage.module.scss";

const DATE_FORMATTER = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const PERSONNEL_PAGE_SIZE = 20;

const UserGroupsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canView = hasPermission(PERMISSIONS.USER_GROUPS_VIEW);
  const canCreate = hasPermission(PERMISSIONS.USER_GROUPS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.USER_GROUPS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.USER_GROUPS_DELETE);

  const [groups, setGroups] = useState<UserGroupListItem[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<UserGroupDetail | null>(null);
  const [detailError, setDetailError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<UserGroupListItem | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<UserGroupMember | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [personnelSearchTerm, setPersonnelSearchTerm] = useState("");
  const [personnelOptions, setPersonnelOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [isSearchingPersonnel, setIsSearchingPersonnel] = useState(false);
  const [isLoadingMorePersonnel, setIsLoadingMorePersonnel] = useState(false);
  const [hasMorePersonnel, setHasMorePersonnel] = useState(false);
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const personnelNextPageRef = useRef(2);
  const personnelSearchRef = useRef("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const syncGroupSnapshot = useCallback((detail: UserGroupDetail) => {
    setSelectedGroup(detail);
    setGroups((prev) =>
      prev.map((group) =>
        group.id === detail.id
          ? {
              ...group,
              name: detail.name,
              description: detail.description ?? null,
              memberCount: detail.members.length,
              createdAt: detail.createdAt,
              updatedAt: detail.updatedAt,
            }
          : group,
      ),
    );
  }, []);

  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userGroupsApi.getList({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm.trim() || undefined,
      });
      setGroups(response.data);
      setTotalRecords(response.total);
      setSelectedGroupId((prev) => {
        if (!response.data.length) return null;
        if (prev && response.data.some((group) => group.id === prev)) return prev;
        return response.data[0]?.id ?? null;
      });
    } catch {
      setGroups([]);
      setTotalRecords(0);
      setSelectedGroupId(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchTerm]);

  const fetchGroupDetail = useCallback(async (groupId: string) => {
    setIsDetailLoading(true);
    setDetailError(false);
    try {
      const detail = await userGroupsApi.getById(groupId);
      setSelectedGroup(detail);
    } catch {
      setSelectedGroup(null);
      setDetailError(true);
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    fetchGroups();
  }, [canView, fetchGroups]);

  useEffect(() => {
    if (!selectedGroupId) {
      setSelectedGroup(null);
      setDetailError(false);
      return;
    }
    fetchGroupDetail(selectedGroupId);
  }, [selectedGroupId, fetchGroupDetail]);

  useEffect(() => {
    setSelectedPersonnelIds([]);
    setPersonnelSearchTerm("");
    personnelNextPageRef.current = 2;
    setHasMorePersonnel(false);
  }, [selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId || !selectedGroup || !canUpdate) {
      setPersonnelOptions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearchingPersonnel(true);
      try {
        const { data, total } = await getAdminUsers({
          page: 1,
          limit: PERSONNEL_PAGE_SIZE,
          search: personnelSearchTerm.trim() || undefined,
          sortBy: "createdAt",
          sortOrder: "desc",
        });

        if (cancelled) return;

        const currentMemberIds = new Set(selectedGroup.members.map((member) => member.id));
        const nextOptions = data
          .filter((user) => !currentMemberIds.has(user.id))
          .map((user) => ({
            value: user.id,
            label:
              [user.firstName, user.lastName].filter(Boolean).join(" ") ||
              user.email ||
              user.identityNumber ||
              user.id,
          }));

        setPersonnelOptions((prev) => {
          const preserved = prev.filter(
            (option) =>
              selectedPersonnelIds.includes(option.value) &&
              !nextOptions.some((nextOption) => nextOption.value === option.value),
          );
          return [...preserved, ...nextOptions];
        });
        personnelNextPageRef.current = 2;
        personnelSearchRef.current = personnelSearchTerm.trim();
        setHasMorePersonnel(data.length < total);
      } catch {
        if (!cancelled) {
          setPersonnelOptions((prev) =>
            prev.filter((option) => selectedPersonnelIds.includes(option.value)),
          );
          setHasMorePersonnel(false);
          toast.error(t("userGroups.memberSearchError"));
        }
      } finally {
        if (!cancelled) {
          setIsSearchingPersonnel(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    canUpdate,
    personnelSearchTerm,
    selectedGroup,
    selectedGroupId,
    selectedPersonnelIds,
    t,
  ]);

  const handleLoadMorePersonnel = useCallback(async (): Promise<boolean> => {
    if (!selectedGroup || isLoadingMorePersonnel || !hasMorePersonnel) return false;

    const page = personnelNextPageRef.current;
    setIsLoadingMorePersonnel(true);
    try {
      const { data, total } = await getAdminUsers({
        page,
        limit: PERSONNEL_PAGE_SIZE,
        search: personnelSearchRef.current || undefined,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      const currentMemberIds = new Set(selectedGroup.members.map((member) => member.id));
      const nextOptions = data
        .filter((user) => !currentMemberIds.has(user.id))
        .map((user) => ({
          value: user.id,
          label:
            [user.firstName, user.lastName].filter(Boolean).join(" ") ||
            user.email ||
            user.identityNumber ||
            user.id,
        }));

      setPersonnelOptions((prev) => {
        const existing = new Set(prev.map((option) => option.value));
        return [
          ...prev,
          ...nextOptions.filter((option) => !existing.has(option.value)),
        ];
      });
      personnelNextPageRef.current = page + 1;
      const more = page * PERSONNEL_PAGE_SIZE < total && data.length > 0;
      setHasMorePersonnel(more);
      return more;
    } catch {
      setHasMorePersonnel(false);
      return false;
    } finally {
      setIsLoadingMorePersonnel(false);
    }
  }, [hasMorePersonnel, isLoadingMorePersonnel, selectedGroup]);

  const selectedPersonnelLabel = useMemo(() => {
    if (selectedPersonnelIds.length === 0) return undefined;
    return selectedPersonnelIds
      .map(
        (id) =>
          personnelOptions.find((option) => option.value === id)?.label ?? id,
      )
      .join(", ");
  }, [personnelOptions, selectedPersonnelIds]);

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleCreate = async (payload: { name: string }) => {
    setIsCreating(true);
    try {
      const created = await userGroupsApi.create({ name: payload.name });
      toast.success(t("userGroups.createSuccess"));
      setIsCreateModalOpen(false);
      await fetchGroups();
      setSelectedGroupId(created.id);
      setSelectedGroup(created);
    } catch {
      /* interceptor handles error toast */
    } finally {
      setIsCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await userGroupsApi.delete(deleteTarget.id);
      toast.success(t("userGroups.deleteSuccess"));
      if (selectedGroupId === deleteTarget.id) {
        setSelectedGroupId(null);
        setSelectedGroup(null);
      }
      setDeleteTarget(null);
      await fetchGroups();
    } catch {
      /* interceptor handles error toast */
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedGroup || selectedPersonnelIds.length === 0) return;

    const currentMemberIds = new Set(selectedGroup.members.map((member) => member.id));
    const nextUserIds = selectedPersonnelIds.filter((id) => !currentMemberIds.has(id));
    if (nextUserIds.length === 0) {
      setSelectedPersonnelIds([]);
      return;
    }

    setIsAddingMembers(true);
    try {
      const updated = await userGroupsApi.addMembers(selectedGroup.id, {
        userIds: nextUserIds,
      });
      syncGroupSnapshot(updated);
      setSelectedPersonnelIds([]);
      toast.success(t("userGroups.addMembersSuccess"));
    } catch {
      /* interceptor handles error toast */
    } finally {
      setIsAddingMembers(false);
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!selectedGroup || !memberToRemove) return;
    setIsRemovingMember(true);
    try {
      const updated = await userGroupsApi.removeMember(selectedGroup.id, memberToRemove.id);
      syncGroupSnapshot(updated);
      setMemberToRemove(null);
      toast.success(t("userGroups.removeMemberSuccess"));
    } catch {
      /* interceptor handles error toast */
    } finally {
      setIsRemovingMember(false);
    }
  };

  const columns: TableColumn<UserGroupListItem>[] = useMemo(
    () => [
      {
        header: t("userGroups.columns.name"),
        accessor: (row) => (
          <span className={styles.nameCell}>
            <span className={styles.iconWrap}>
              <GroupIcon width={16} height={16} />
            </span>
            <span className={styles.groupName}>{row.name}</span>
          </span>
        ),
        className: styles.nameColumn,
      },
      {
        header: t("userGroups.columns.members"),
        accessor: (row) => (
          <span className={styles.memberCountBadge}>{row.memberCount}</span>
        ),
        className: styles.membersColumn,
      },
      {
        header: t("userGroups.columns.createdAt"),
        accessor: (row) =>
          row.createdAt ? DATE_FORMATTER.format(new Date(row.createdAt)) : "—",
        className: styles.dateColumn,
      },
    ],
    [t],
  );

  const renderActions = (row: UserGroupListItem) => {
    if (!canDelete && !canUpdate) return null;
    const isOpen = openDropdowns[row.id] || false;

    return (
      <Popover
        content={
          <div className={styles.actionMenu}>
            {canUpdate && (
              <button
                type="button"
                className={styles.actionMenuItem}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDropdownToggle(row.id, false);
                  setSelectedGroupId(row.id);
                }}
              >
                <EditIcon width={20} height={20} />
                {t("userGroups.actions.manageMembers")}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className={`${styles.actionMenuItem} ${styles.error}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDropdownToggle(row.id, false);
                  setDeleteTarget(row);
                }}
              >
                <DeleteIcon width={20} height={20} />
                {t("userGroups.actions.delete")}
              </button>
            )}
          </div>
        }
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(row.id, false)}
        position="bottom"
      >
        <button
          type="button"
          className={styles.actionButton}
          onClick={(event) => {
            event.stopPropagation();
            handleDropdownToggle(row.id, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  if (!canView) {
    return <Navigate to="/dashboard/unauthorized" replace />;
  }

  return (
    <DashboardLayout>
      <Header title={t("userGroups.title")} />
      <PageHeader
        searchPlaceholder={t("userGroups.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={setSearchTerm}
        onAddNew={canCreate ? () => setIsCreateModalOpen(true) : undefined}
        addButtonLabel={canCreate ? t("userGroups.addButton") : undefined}
      />

      <div className={styles.contentGrid}>
        <div className={styles.tablePanel}>
          <Table
            columns={columns}
            data={groups}
            isLoading={isLoading}
            getRowId={(row) => row.id}
            totalRecords={totalRecords}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            onRowClick={(row) => setSelectedGroupId(row.id)}
            renderActions={renderActions}
            openDropdowns={openDropdowns}
            emptyMessage={t("userGroups.noData")}
            contentBasedHeight
          />
        </div>

        <aside className={styles.detailPanel}>
          {isDetailLoading ? (
            <p className={styles.detailHint}>{t("userGroups.loadingDetail")}</p>
          ) : detailError ? (
            <p className={styles.detailHint}>{t("userGroups.detailError")}</p>
          ) : selectedGroup ? (
            <UserGroupMembersPanel
              groupName={selectedGroup.name}
              members={selectedGroup.members}
              canUpdate={canUpdate}
              personnelOptions={personnelOptions}
              selectedPersonnelIds={selectedPersonnelIds}
              selectedPersonnelLabel={selectedPersonnelLabel}
              isSearchingPersonnel={isSearchingPersonnel}
              isLoadingMorePersonnel={isLoadingMorePersonnel}
              hasMorePersonnel={hasMorePersonnel}
              isAddingMembers={isAddingMembers}
              removingMemberId={isRemovingMember ? memberToRemove?.id ?? null : null}
              onPersonnelSearch={setPersonnelSearchTerm}
              onPersonnelSelect={setSelectedPersonnelIds}
              onLoadMorePersonnel={handleLoadMorePersonnel}
              onAddMembers={handleAddMembers}
              onRemoveMember={(member) => setMemberToRemove(member)}
            />
          ) : (
            <p className={styles.detailHint}>{t("userGroups.selectGroup")}</p>
          )}
        </aside>
      </div>

      <UserGroupCreateModal
        isOpen={isCreateModalOpen}
        loading={isCreating}
        onClose={() => {
          if (isCreating) return;
          setIsCreateModalOpen(false);
        }}
        onSubmit={handleCreate}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t("userGroups.deleteConfirmTitle")}
        message={t("userGroups.deleteConfirmMessage")}
        confirmText={t("buttons.delete")}
        cancelText={t("buttons.cancel")}
        loading={isDeleting}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={!!memberToRemove}
        onClose={() => !isRemovingMember && setMemberToRemove(null)}
        onConfirm={handleConfirmRemoveMember}
        title={t("userGroups.members.removeConfirmTitle")}
        message={
          memberToRemove
            ? `${
                [memberToRemove.firstName, memberToRemove.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                memberToRemove.email ||
                memberToRemove.identityNumber ||
                memberToRemove.id
              } — ${t("userGroups.members.removeConfirmMessage")}`
            : ""
        }
        confirmText={t("userGroups.members.removeButton")}
        cancelText={t("buttons.cancel")}
        loading={isRemovingMember}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default UserGroupsPage;
